// ────────────────────────────────────────────────────────────────────
// Shared Deep Dive render helpers (Feb 2026).
// Consolidates the presentation logic used by both the Cohort Deep
// Dive modal (ViatrisClientDashboard.js) and the Reference Library
// Deep Dive modal (ViatrisReferenceLibraryPage.js). Before this file
// existed each surface had its own duplicated CitedText/SectionRef/
// ReferenceRail — they drifted over time. Everything the two modals
// use to render assessment content lives here now.
//
// Design principles (per client's Feb 2026 quality spec):
//   • Never JSON.stringify anything the user sees.
//   • Strip literal markdown that leaks through Claude/Perplexity.
//   • Replace generic key-value grids with named blocks (Company
//     profile, Competition tables) whenever we recognise the schema.
//   • Fall back gracefully to the generic renderer when the payload
//     doesn't match a known schema — never blow up on unexpected keys.
//   • Empty fields render as "Public information currently unavailable."
//     not "—" or blank space.
// ────────────────────────────────────────────────────────────────────

import React, { useEffect, useRef } from 'react';
import { BookOpen } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

// ────────────────────────────────────────────────────────────────────
// Markdown scrubbing — Claude/Perplexity occasionally leak literal
// **bold**, *italic*, __underline__, ## headings, and hyphen-bullets.
// The client's premium dossier standard requires none of these render
// as literal symbols. Convert to inline React nodes.
//
// This is intentionally narrow — full-blown Markdown-to-HTML is out
// of scope (pulls in a heavyweight dep and lets renderer edge cases
// break the layout). We handle the three offenders we've actually
// seen in production data.
// ────────────────────────────────────────────────────────────────────

// Strip standalone markdown symbols that Perplexity/Claude sometimes
// leak into JSON string fields (e.g., "**Key finding**: efficacy was ..."
// where the ** were meant as bold delimiters for a caller that doesn't
// render markdown). Returns a plain string.
export function scrubMarkdownSymbols(s) {
  if (s == null) return '';
  let t = String(s);
  // Strip leading heading markers on individual lines (## Title → Title)
  t = t.replace(/^\s*#{1,6}\s+/gm, '');
  // Strip literal bullet markers at line start ("- foo" or "* foo" → foo).
  // Only when followed by a space so URLs like "*-branch" survive.
  t = t.replace(/^\s*[-*]\s+/gm, '');
  // Backticks used decoratively (not code — Perplexity sometimes wraps
  // drug names in backticks). We render as plain text.
  t = t.replace(/`+/g, '');
  return t;
}

// Split a string on markdown emphasis (**bold**, *italic*, __bold__) and
// return an array of React nodes with the appropriate styling. Handles
// nested/adjacent emphasis reasonably. Applied AFTER scrubMarkdownSymbols
// so a scrub of "**Key**" that leaves "Key" won't double-render.
function _splitEmphasis(text, keyPrefix = '') {
  if (!text) return [];
  // Non-greedy match on **...** first (bold), then *...* (italic).
  // We build a mini state machine walking the string.
  const out = [];
  const re = /(\*\*[^*\n]+?\*\*)|(__[^_\n]+?__)|(\*[^*\n]+?\*)|(_[^_\n]+?_)/g;
  let m; let last = 0; let k = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**') || tok.startsWith('__')) {
      out.push(<strong key={`${keyPrefix}b${k++}`} className="font-semibold text-slate-900">{tok.slice(2, -2)}</strong>);
    } else {
      out.push(<em key={`${keyPrefix}i${k++}`} className="italic">{tok.slice(1, -1)}</em>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// ────────────────────────────────────────────────────────────────────
// Citation display mode (Jul 2026).
//
// The platform stores AI narrative WITH inline [N] markers so the
// statement↔source mapping is never lost. How those markers are
// PRESENTED is a render-time decision controlled by a single mode:
//
//   • "bibliography" (DEFAULT) — inline [N] markers are stripped from
//     prose and the per-section "References cited here" strips are
//     hidden. Sources live only in the consolidated Bibliography tab.
//     This is the client-facing / reviewer default: a clean executive
//     due-diligence report.
//   • "citation" — inline [N] chips are rendered (legacy behaviour),
//     for internal scientific review.
//
// Nothing is regenerated when the mode flips — markers are hidden, not
// deleted. Default is resolved via (in priority order): an explicit
// <CitationModeContext.Provider>, a `fr_citation_mode` localStorage
// override (hidden dev/config flag), then "bibliography".
// ────────────────────────────────────────────────────────────────────
export const CITATION_MODES = { BIBLIOGRAPHY: 'bibliography', CITATION: 'citation' };
export const DEFAULT_CITATION_MODE = CITATION_MODES.BIBLIOGRAPHY;
export const CitationModeContext = React.createContext(null);

export function useCitationMode() {
  const ctx = React.useContext(CitationModeContext);
  if (ctx === CITATION_MODES.BIBLIOGRAPHY || ctx === CITATION_MODES.CITATION) return ctx;
  try {
    const v = window.localStorage.getItem('fr_citation_mode');
    if (v === CITATION_MODES.CITATION || v === CITATION_MODES.BIBLIOGRAPHY) return v;
  } catch (e) { /* SSR / access denied — fall through */ }
  return DEFAULT_CITATION_MODE;
}

// Remove inline [N] citation markers (and their separators/leading
// whitespace) from a string, then tidy punctuation/spacing so the
// prose reads cleanly. Newlines are preserved (content is rendered
// with whitespace-pre-line).
const _STRIP_CITE_RE = /[ \t]*(?:[,;]?[ \t]*\[\d{1,3}\])+/g;
export function stripCitationMarkers(s) {
  if (s == null) return '';
  return String(s)
    .replace(_STRIP_CITE_RE, '')
    .replace(/\(\s*\)/g, '')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/[ \t]+([.,;:!?)])/g, '$1')
    .trim();
}

// Best-effort publisher/website label for a reference — derived from an
// explicit `publisher` field when present, else the URL's hostname.
export function refPublisher(ref) {
  if (!ref) return '';
  if (ref.publisher) return String(ref.publisher);
  let d = ref.domain || '';
  if (!d && ref.url) {
    try { d = new URL(ref.url).hostname; } catch (e) { d = ''; }
  }
  return String(d).replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '');
}

// ────────────────────────────────────────────────────────────────────
// Inline citations: replace [N] tokens in prose with clickable chips
// that flash the corresponding reference on the right-rail library.
// Also handles markdown emphasis inside the same string.
// In Bibliography Mode the [N] markers are stripped and the prose is
// rendered clean.
// ────────────────────────────────────────────────────────────────────

const _CITE_RE = /\[(\d{1,3})\]/g;

export function CitedText({ text, onCite, maxRef }) {
  const mode = useCitationMode();
  if (text == null || text === '') return null;
  if (mode === CITATION_MODES.BIBLIOGRAPHY) {
    const clean = stripCitationMarkers(scrubMarkdownSymbols(String(text)));
    if (!clean) return null;
    return <span className="whitespace-pre-line">{_splitEmphasis(clean, 'bib-')}</span>;
  }
  const scrubbed = scrubMarkdownSymbols(String(text));
  const nodes = [];
  let key = 0;
  let last = 0;
  let m;
  _CITE_RE.lastIndex = 0;
  while ((m = _CITE_RE.exec(scrubbed)) !== null) {
    if (m.index > last) {
      const chunk = scrubbed.slice(last, m.index);
      // Render emphasis inside every non-citation chunk.
      const parts = _splitEmphasis(chunk, `k${key}-`);
      parts.forEach((p) => nodes.push(<React.Fragment key={key++}>{p}</React.Fragment>));
    }
    const n = Number(m[1]);
    if (Number.isInteger(n) && n >= 1 && n <= (maxRef || 999)) {
      nodes.push(
        <button
          key={key++}
          type="button"
          onClick={(e) => { e.stopPropagation(); onCite && onCite(n); }}
          className="inline-flex items-center align-baseline mx-0.5 px-1 py-[1px] rounded text-[10px] font-semibold bg-indigo-50 text-indigo-800 border border-indigo-200 hover:bg-indigo-100 transition-colors"
          data-testid={`cite-${n}`}
          title={`Jump to reference [${n}]`}
        >
          [{n}]
        </button>
      );
    } else {
      nodes.push(<span key={key++} className="text-slate-400">{m[0]}</span>);
    }
    last = m.index + m[0].length;
  }
  if (last < scrubbed.length) {
    const parts = _splitEmphasis(scrubbed.slice(last), `tail-${key}-`);
    parts.forEach((p) => nodes.push(<React.Fragment key={key++}>{p}</React.Fragment>));
  }
  return <span className="whitespace-pre-line">{nodes}</span>;
}

// ────────────────────────────────────────────────────────────────────
// Empty-value fallback — replaces every "—" / blank / null placeholder
// with the executive-standard "Public information currently unavailable."
// The client's Feb 2026 spec explicitly bans "—" as a data-absence signal.
// ────────────────────────────────────────────────────────────────────
export const EMPTY_LABEL = 'Public information currently unavailable.';

export function EmptyValue() {
  return <span className="text-slate-400 italic text-xs">{EMPTY_LABEL}</span>;
}

function _isEmpty(v) {
  if (v == null) return true;
  if (typeof v === 'string') return v.trim() === '' || v.trim() === '—' || v.trim() === '-' || v.trim().toLowerCase() === 'unknown';
  if (Array.isArray(v)) return v.length === 0 || v.every(_isEmpty);
  if (typeof v === 'object') return Object.keys(v).length === 0;
  return false;
}

// ────────────────────────────────────────────────────────────────────
// renderValue — the generic "I don't know what this is, render it nicely"
// helper. Never JSON.stringify. Never leak raw dicts to the screen.
// ────────────────────────────────────────────────────────────────────
export function renderValue(val, onCite, maxRef, depth = 0) {
  if (_isEmpty(val)) return <EmptyValue />;
  if (Array.isArray(val)) {
    // If it's an array of objects with a common shape, render as a
    // compact table. Otherwise render as bullets.
    const objectRows = val.filter((v) => v && typeof v === 'object' && !Array.isArray(v));
    if (objectRows.length === val.length && val.length > 0) {
      // Union of keys, capped so we don't blow the column count.
      const keys = Array.from(new Set(val.flatMap((r) => Object.keys(r)))).slice(0, 6);
      return (
        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-xs border border-slate-200 rounded">
            <thead className="bg-slate-100/70 text-slate-600">
              <tr>
                {keys.map((k) => (
                  <th key={k} className="text-left px-2 py-1 font-semibold capitalize border-b border-slate-200">
                    {k.replace(/_/g, ' ')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {val.map((row, i) => (
                <tr key={i} className="border-b border-slate-100 last:border-b-0 align-top">
                  {keys.map((k) => (
                    <td key={k} className="px-2 py-1.5 text-slate-700">
                      {_isEmpty(row[k])
                        ? <EmptyValue />
                        : (typeof row[k] === 'object'
                            ? renderValue(row[k], onCite, maxRef, depth + 1)
                            : <CitedText text={String(row[k])} onCite={onCite} maxRef={maxRef} />)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    return (
      <ul className="list-disc pl-5 text-sm text-slate-700 space-y-0.5">
        {val.map((v, i) => (
          <li key={i}>
            {typeof v === 'object' && v !== null
              ? renderValue(v, onCite, maxRef, depth + 1)
              : <CitedText text={String(v)} onCite={onCite} maxRef={maxRef} />}
          </li>
        ))}
      </ul>
    );
  }
  if (typeof val === 'object') {
    return (
      <div className={`space-y-1 text-xs text-slate-700 ${depth > 0 ? 'pl-2 border-l border-slate-100' : ''}`}>
        {Object.entries(val).map(([k, v]) => (
          <div key={k} className="flex flex-col md:flex-row md:gap-2">
            <strong className="capitalize text-slate-500 text-[10px] uppercase tracking-wide md:min-w-[110px] md:pt-0.5">
              {k.replace(/_/g, ' ')}
            </strong>
            <div className="flex-1 min-w-0">
              {typeof v === 'object' && v !== null
                ? renderValue(v, onCite, maxRef, depth + 1)
                : (_isEmpty(v)
                    ? <EmptyValue />
                    : <CitedText text={String(v)} onCite={onCite} maxRef={maxRef} />)}
            </div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <p className="text-sm text-slate-700 leading-relaxed">
      <CitedText text={String(val)} onCite={onCite} maxRef={maxRef} />
    </p>
  );
}

// ────────────────────────────────────────────────────────────────────
// Section-scoped reference strip.
// ────────────────────────────────────────────────────────────────────
export function SectionRefStrip({ refIds, onCite }) {
  const mode = useCitationMode();
  if (mode === CITATION_MODES.BIBLIOGRAPHY) return null;
  if (!refIds || !refIds.length) return null;
  return (
    <div className="mt-3 pt-2 border-t border-slate-100 text-[10px] text-slate-500 flex items-center gap-1.5 flex-wrap" data-testid="section-ref-strip">
      <span className="uppercase tracking-wide font-semibold text-slate-500">References cited here</span>
      {refIds.map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onCite && onCite(n)}
          className="inline-flex items-center px-1.5 py-[1px] rounded text-[10px] font-semibold bg-indigo-50 text-indigo-800 border border-indigo-200 hover:bg-indigo-100"
          data-testid={`section-cite-${n}`}
        >
          [{n}]
        </button>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Reference Library rail.
// ────────────────────────────────────────────────────────────────────
export function ReferenceRail({ refs, highlightN, onDismissHighlight }) {
  const containerRef = useRef(null);
  useEffect(() => {
    if (!highlightN) return;
    const el = containerRef.current?.querySelector(`[data-ref-n="${highlightN}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-indigo-500', 'ring-offset-1');
      const t = setTimeout(() => {
        el.classList.remove('ring-2', 'ring-indigo-500', 'ring-offset-1');
        if (onDismissHighlight) onDismissHighlight();
      }, 1600);
      return () => clearTimeout(t);
    }
  }, [highlightN, onDismissHighlight]);

  if (!refs || !refs.length) {
    return (
      <div className="p-3 text-[11px] text-slate-500 italic border-l border-slate-200">
        No sources captured for this asset yet.
      </div>
    );
  }
  return (
    <div className="h-full flex flex-col border-l border-slate-200 bg-slate-50/40" data-testid="reference-rail">
      <div className="px-3 py-2 border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-1.5">
          <BookOpen className="w-3 h-3 text-slate-500" />
          Bibliography
          <Badge variant="outline" className="ml-auto text-[9px] px-1.5 py-0">{refs.length}</Badge>
        </div>
      </div>
      <ol ref={containerRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5">
        {refs.map((r) => {
          const publisher = refPublisher(r);
          return (
            <li
              key={r.n}
              data-ref-n={r.n}
              className="text-[11px] border border-slate-200 rounded bg-white px-2 py-1.5 transition-shadow"
              data-testid={`ref-item-${r.n}`}
            >
              <div className="flex items-start gap-1.5">
                <span className="text-[10px] font-bold text-indigo-700 shrink-0 min-w-[18px]">{r.n}.</span>
                <div className="min-w-0 flex-1">
                  <div className="text-slate-800 leading-snug">
                    {publisher && <span className="font-semibold">{publisher}. </span>}
                    {r.title && <span>{r.title}.</span>}
                    {!publisher && !r.title && <span className="text-slate-500">Source</span>}
                    {r.year && <span className="text-slate-400"> Accessed {r.year}.</span>}
                  </div>
                  {r.url && (
                    <a href={r.url} target="_blank" rel="noreferrer" className="text-indigo-700 hover:underline break-all text-[10px] leading-tight block mt-0.5">
                      {r.url}
                    </a>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// CompanyProfileBlock — renders the 13 canonical company fields in a
// premium dossier layout with two-column grid on desktop. Missing
// fields render "Public information currently unavailable." per the
// client's Feb 2026 quality standard.
// ────────────────────────────────────────────────────────────────────
const COMPANY_FIELDS = [
  { key: 'company_overview',     label: 'Company Overview',      wide: true },
  { key: 'headquarters',         label: 'Headquarters' },
  { key: 'founding_year',        label: 'Founded' },
  { key: 'employee_size',        label: 'Employee Size' },
  { key: 'ownership',            label: 'Ownership' },
  { key: 'therapeutic_focus',    label: 'Therapeutic Focus' },
  { key: 'pipeline_overview',    label: 'Pipeline Summary',      wide: true },
  { key: 'existing_partnerships', label: 'Key Partnerships' },
  { key: 'investors_funding',    label: 'Funding History' },
  { key: 'ipo_status',           label: 'IPO Status' },
  { key: 'licensing_history',    label: 'Licensing History',     wide: true },
  { key: 'notable_milestones',   label: 'Recent News & Milestones', wide: true },
  { key: 'bd_contact',           label: 'BD Contact' },
  { key: 'website',              label: 'Website' },
];

export function CompanyProfileBlock({ data, refIds, onCite, maxRef }) {
  if (!data || typeof data !== 'object') {
    return <EmptySection title="Company Intelligence" />;
  }
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
        {COMPANY_FIELDS.map((f) => {
          const val = data[f.key];
          return (
            <div key={f.key} className={f.wide ? 'md:col-span-2' : ''}>
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-0.5">{f.label}</div>
              <div className="text-sm text-slate-700 leading-relaxed">
                {_isEmpty(val)
                  ? <EmptyValue />
                  : (typeof val === 'object'
                      ? renderValue(val, onCite, maxRef)
                      : <CitedText text={String(val)} onCite={onCite} maxRef={maxRef} />)}
              </div>
            </div>
          );
        })}
      </div>
      <SectionRefStrip refIds={refIds} onCite={onCite} />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// CompetitionBlock — categorises the Perplexity competitive_landscape
// payload into (1) Approved Therapies (2) Late-stage Pipeline
// (3) Similar-Mechanism Competitors + a Competitive Assessment
// narrative. Falls back to generic renderValue when the payload is a
// legacy string blob instead of arrays.
// ────────────────────────────────────────────────────────────────────
function _coerceCompetitorRows(field) {
  // Perplexity sometimes returns arrays of objects, sometimes prose.
  // Turn a prose blob into a single-column table so the executive
  // dossier still looks clean.
  if (Array.isArray(field)) return field;
  if (typeof field === 'string' && field.trim()) {
    // Split on newlines or semicolons — very forgiving.
    const parts = field
      .split(/\n|;/)
      .map((s) => s.replace(/^[-*•]\s*/, '').trim())
      .filter((s) => s);
    return parts.map((p) => ({ entry: p }));
  }
  return [];
}

export function CompetitionBlock({ data, refIds, onCite, maxRef }) {
  if (!data || typeof data !== 'object' || Object.keys(data).length === 0) {
    return <EmptySection title="Competitive Landscape" />;
  }
  const approved   = _coerceCompetitorRows(data.approved_therapies);
  const lateStage  = _coerceCompetitorRows(data.major_pipeline_competitors || data.late_stage_pipeline);
  const similarMoa = _coerceCompetitorRows(data.similar_mechanism_competitors || data.similar_mechanism);
  const strengths  = data.competitive_strengths;
  const risks      = data.competitive_risks;
  const firstInCls = data.first_in_class_potential;
  const summary    = data.market_position_summary || data.competitive_assessment;

  const renderBucket = (label, rows) => {
    if (!rows.length) return null;
    return (
      <div className="border border-slate-200 rounded overflow-hidden">
        <div className="px-3 py-1.5 bg-slate-100 text-[10px] uppercase tracking-wide text-slate-700 font-bold">
          {label} · <span className="text-slate-500 font-normal normal-case">{rows.length}</span>
        </div>
        <div className="p-2">{renderValue(rows, onCite, maxRef)}</div>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {renderBucket('Approved Therapies', approved)}
      {renderBucket('Late-Stage Pipeline', lateStage)}
      {renderBucket('Similar-Mechanism Competitors', similarMoa)}
      {(strengths || risks || firstInCls) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {!_isEmpty(strengths) && (
            <div className="border border-emerald-200 bg-emerald-50/40 rounded p-3">
              <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-bold mb-1">Competitive Strengths</div>
              {renderValue(strengths, onCite, maxRef)}
            </div>
          )}
          {!_isEmpty(risks) && (
            <div className="border border-rose-200 bg-rose-50/40 rounded p-3">
              <div className="text-[10px] uppercase tracking-wide text-rose-700 font-bold mb-1">Competitive Risks</div>
              {renderValue(risks, onCite, maxRef)}
            </div>
          )}
          {!_isEmpty(firstInCls) && (
            <div className="border border-sky-200 bg-sky-50/40 rounded p-3 md:col-span-2">
              <div className="text-[10px] uppercase tracking-wide text-sky-700 font-bold mb-1">First / Best-in-Class Assessment</div>
              {renderValue(firstInCls, onCite, maxRef)}
            </div>
          )}
        </div>
      )}
      {!_isEmpty(summary) && (
        <div className="border border-indigo-200 bg-indigo-50/40 rounded p-3">
          <div className="text-[10px] uppercase tracking-wide text-indigo-700 font-bold mb-1">Competitive Assessment</div>
          <p className="text-sm text-slate-700 leading-relaxed">
            <CitedText text={String(summary)} onCite={onCite} maxRef={maxRef} />
          </p>
        </div>
      )}
      <SectionRefStrip refIds={refIds} onCite={onCite} />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// EmptySection — replaces the terse "No X captured." message with an
// amber advisory that steers the analyst to the Rescore action.
// ────────────────────────────────────────────────────────────────────
export function EmptySection({ title }) {
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50/50 px-3 py-3 text-sm">
      <div className="font-semibold text-amber-800 mb-1">{EMPTY_LABEL}</div>
      <p className="text-amber-700 text-xs leading-relaxed">
        The latest research pass returned no data for the {title.toLowerCase()} section. Click
        <strong> Run AI </strong> on this asset in the Workbench to regenerate the full assessment,
        or use the section&apos;s <strong>Re-research</strong> action in the Reviewer Workspace to
        refresh just this section.
      </p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// GenericSection — the fall-through renderer used for sections we
// haven't hand-designed. Iterates keys, renders each via renderValue,
// appends the section-ref strip at the bottom.
// ────────────────────────────────────────────────────────────────────
export function GenericSection({ title, data, refIds, onCite, maxRef }) {
  if (!data || Object.keys(data).length === 0) {
    return <EmptySection title={title} />;
  }
  return (
    <div className="space-y-3">
      {Object.entries(data).map(([key, val]) => (
        <div key={key} className="border border-slate-100 rounded p-3">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1 capitalize">
            {key.replace(/_/g, ' ')}
          </div>
          {renderValue(val, onCite, maxRef)}
        </div>
      ))}
      <SectionRefStrip refIds={refIds} onCite={onCite} />
    </div>
  );
}
